import { useEffect, useRef } from 'react'
import type { dia } from '@joint/core'
import { useFloorStore } from '../../../map-editor-shared/store/floorStore'

interface UseGraphAutoSaveParams {
  graph: dia.Graph | null
  currentFloor: string | null
  isRestoring: boolean
  updateFloorMapData: (floorId: string, data: any) => void
}

/**
 * Auto-saves graph changes to localStorage with 1-second debounce.
 * Listens to graph events (position, size, attrs, add, remove, change) and
 * saves the graph JSON to floor map data immediately to localStorage.
 *
 * Note: Server save is handled separately by handleBeforeFloorChange when switching floors.
 */
export const useGraphAutoSave = ({
  graph,
  currentFloor,
  isRestoring,
  updateFloorMapData,
}: UseGraphAutoSaveParams) => {
  const autoSaveTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const isFloorSwitching = useFloorStore(state => state.isFloorSwitching)

  useEffect(() => {
    if (!graph || !currentFloor || isRestoring || isFloorSwitching) return

    const saveGraphToFloor = () => {
      if (isRestoring || useFloorStore.getState().isFloorSwitching) return

      const json = graph.toJSON()
      if (!json.cells || json.cells.length === 0) return

      updateFloorMapData(currentFloor, {
        graphJson: json,
      })
    }

    const debouncedLocalSave = () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current)
      }
      autoSaveTimeoutRef.current = setTimeout(saveGraphToFloor, 1000)
    }

    const handleGraphChange = () => {
      if (!isRestoring && !useFloorStore.getState().isFloorSwitching) {
        debouncedLocalSave()
      }
    }

    // 'change' is superset of change:position/size/attrs — no need for specific listeners
    graph.on('change', handleGraphChange)
    graph.on('add', handleGraphChange)
    graph.on('remove', handleGraphChange)

    return () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current)
        autoSaveTimeoutRef.current = null
      }
      graph.off('change', handleGraphChange)
      graph.off('add', handleGraphChange)
      graph.off('remove', handleGraphChange)
    }
  }, [graph, currentFloor, updateFloorMapData, isRestoring, isFloorSwitching])
}
